import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingCourseViewerPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [course, setCourse] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [activeBank, setActiveBank] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const load = useCallback(() => {
    trainingApi.getCourseForEmployee(courseId).then((r) => setCourse(r.data)).catch(() => {});
    trainingApi.myAssignments().then((r) => setAssignment(r.data.find((a) => a.course_id === courseId) || null)).catch(() => {});
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (!course) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;
  if (course.content_pending_approval) {
    return <p className="text-sm text-amber-700" data-testid="course-content-pending">{t("courseViewer.pendingApproval")}</p>;
  }

  const completedLessonIds = assignment?.completed_lesson_ids || [];

  const completeLesson = async (lessonId) => {
    if (!assignment) { toast.error(t("courseViewer.noAssignmentError")); return; }
    try { await trainingApi.completeLesson(assignment.id, lessonId); toast.success(t("courseViewer.lessonMarkedComplete")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startQuiz = async (bank) => {
    try {
      const { data } = await trainingApi.getQuestionsForEmployee(bank.id);
      setQuestions(data);
      setAnswers({});
      setResult(null);
      setActiveBank(bank);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitQuiz = async () => {
    try {
      const { data } = await trainingApi.submitQuizAttempt(activeBank.id, assignment?.id, answers);
      setResult(data);
      toast[data.passed ? "success" : "error"](t(data.passed ? "courseViewer.passedToast" : "courseViewer.failedToast", { score: data.score }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const acknowledge = async () => {
    try { await trainingApi.acknowledgeCourse(courseId); setAcknowledged(true); toast.success(t("courseViewer.acknowledgeSuccess")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="course-viewer-page">
      <PageHelpBanner pageKey="course-viewer" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.courseViewer")}</p>
      </PageHelpBanner>
      <button data-testid="back-to-my-training-btn" onClick={() => navigate("/employee/training")} className="text-xs font-semibold text-gray-500 hover:text-gray-800">← {t("courseViewer.back")}</button>
      <div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900">{course.title_en}</h2>
        {assignment && <p className="text-sm text-gray-500">{t("courseViewer.status")}: {t(`statuses.${assignment.status}`)}</p>}
      </div>

      {!activeBank && (
        <>
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("courseViewer.lessons")}</p>
            {(course.lessons || []).map((l) => (
              <div key={l.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`viewer-lesson-${l.id}`}>
                <p className="text-sm font-semibold text-gray-800">{l.title_en}</p>
                {(l.blocks || []).map((b, i) => <p key={i} className="text-sm text-gray-600 mt-2">{b.text_en}</p>)}
                {completedLessonIds.includes(l.id) ? (
                  <span className="text-xs font-semibold text-emerald-600 mt-2 inline-block" data-testid={`lesson-completed-badge-${l.id}`}>{t("courseViewer.lessonCompleted")}</span>
                ) : (
                  <button data-testid={`complete-lesson-btn-${l.id}`} onClick={() => completeLesson(l.id)} className="mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("courseViewer.markComplete")}</button>
                )}
              </div>
            ))}
            {(course.lessons || []).length === 0 && <p className="text-sm text-gray-500" data-testid="no-viewer-lessons">{t("courseViewer.noLessons")}</p>}
          </div>

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("courseViewer.quiz")}</p>
            {(course.quiz_banks || []).map((b) => (
              <button key={b.id} data-testid={`start-quiz-btn-${b.id}`} onClick={() => startQuiz(b)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-colors">
                <p className="text-sm font-semibold text-gray-800">{b.title_en}</p>
                <p className="text-xs text-gray-500">{t("courseViewer.passScore")}: {b.proposed_pass_score}%</p>
              </button>
            ))}
            {(course.quiz_banks || []).length === 0 && <p className="text-sm text-gray-500" data-testid="no-viewer-quizzes">{t("courseViewer.noQuiz")}</p>}
          </div>

          {assignment?.status === "COMPLETED" && (
            <button data-testid="acknowledge-course-btn" disabled={acknowledged} onClick={acknowledge} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              {acknowledged ? t("courseViewer.acknowledged") : t("courseViewer.signAcknowledgment")}
            </button>
          )}
        </>
      )}

      {activeBank && !result && (
        <div className="space-y-4" data-testid="quiz-taking-panel">
          {questions.map((q) => (
            <div key={q.id} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`quiz-question-${q.id}`}>
              <p className="text-sm font-semibold text-gray-800 mb-2">{q.text_en}</p>
              {(q.options || []).map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm text-gray-700 py-1" data-testid={`quiz-option-${o.id}`}>
                  <input type="radio" name={q.id} checked={answers[q.id] === o.id} onChange={() => setAnswers({ ...answers, [q.id]: o.id })} />
                  {o.text_en}
                </label>
              ))}
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="submit-quiz-btn" onClick={submitQuiz} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("courseViewer.submitQuiz")}</button>
            <button data-testid="cancel-quiz-btn" onClick={() => setActiveBank(null)} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("courseViewer.cancel")}</button>
          </div>
        </div>
      )}

      {result && (
        <div className={`rounded-xl p-6 border ${result.passed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="quiz-result-panel">
          <p className="text-lg font-bold" data-testid="quiz-result-score">{result.score}%</p>
          <p className={`text-sm font-semibold ${result.passed ? "text-emerald-700" : "text-red-700"}`} data-testid="quiz-result-status">
            {result.passed ? t("courseViewer.passedAwaitingReview") : t("courseViewer.failedRetraining")}
          </p>
          <button data-testid="quiz-result-back-btn" onClick={() => { setActiveBank(null); setResult(null); }} className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("courseViewer.backToCourse")}</button>
        </div>
      )}
    </div>
  );
}
